import {Breadcrumb, Empty, Pagination, Spin} from 'antd';
import {useContext, useEffect, useMemo, useState} from 'react';
import {Link} from 'react-router-dom';

import {FilterContext} from '../../context/FilterContext/FilterContext';
import {
  RQGetProductsByFilter,
  RqGetActiveOffers,
  RqGetAllProducts,
} from '../../reactQuery/apis';
import SelectedFilters from '../../screenComponent/ProductScreen/SelectedFilters/SelectedFilters';

import {FilteredProducts} from './FilteredProducts';

const ProductScreen = () => {
  const {data: products = [], isLoading, error} = RqGetAllProducts();

  const {data: offers = []} = RqGetActiveOffers();

  const [sortOption, setSortOption] = useState('New Arrivals');

  const [currentPage, setCurrentPage] = useState(1);

  const {filters, setFilters, resetFilters} = useContext(FilterContext);

  const pageSize = 12;

  const getApiFilters = filters => {

    const apiFilters = {
      
    };

    if (filters?.jewelryType?.length) {
      apiFilters.jewelryType = filters.jewelryType[0];
    }

    if (filters?.productType?.length) {
      apiFilters.productType = filters.productType[0];
    }

    if (filters?.minPrice !== '') {
      apiFilters.minPrice = Number(filters.minPrice);
    }

    if (filters?.maxPrice !== '') {
      apiFilters.maxPrice = Number(filters.maxPrice);
    }

    return apiFilters;
  };

  const apiFilters = useMemo(() => getApiFilters(filters), [filters]);

  const hasActiveFilters = Object.keys(apiFilters).length > 0;

  const {data: filteredProducts = []} = RQGetProductsByFilter(apiFilters, {
    enabled: hasActiveFilters,
  });

  useEffect(() => {
    setCurrentPage(1);
  }, [filters, sortOption]);

  const productsToUse = hasActiveFilters ? filteredProducts : products;

  const sortedProducts = useMemo(() => {
    const sorted = [...productsToUse];

    switch (sortOption) {
      case 'Price: Low to High':
        return sorted.sort(
          (a, b) =>
            Number(a?.size?.[0]?.price || 0) -
            Number(b?.size?.[0]?.price || 0),
        );

      case 'Price: High to Low':
        return sorted.sort(
          (a, b) =>
            Number(b?.size?.[0]?.price || 0) -
            Number(a?.size?.[0]?.price || 0),
        );

      case 'New Arrivals':
      default:
        return sorted.sort(
          (a, b) => new Date(b.createdOn) - new Date(a.createdOn),
        );
    }
  }, [productsToUse, sortOption]);

  const startIndex = (currentPage - 1) * pageSize;

  const paginatedProducts = sortedProducts.slice(
    startIndex,
    startIndex + pageSize,
  );

  const handlePageChange = page => {
    setCurrentPage(page);
  };

  const breadcrumbItems = [
    {
      title: <Link to={'/'}>Home</Link>,
    },
    {
      title: 'Products',
    },
  ];

  if (isLoading) {
    return (
      <div className={'flex justify-center mt-20'}>
        <Spin size={'large'} />
      </div>
    );
  }

  if (error) {
    return <div>Error loading products</div>;
  }

  return (
    <div className={'w-full flex justify-center lg:p-20 p-4'}>
      <div className={'max-w-7xl w-full'}>

        <Breadcrumb
          separator={'>'}
          items={breadcrumbItems}
          className={'mb-4'}
        />

        <FilteredProducts
          sortOption={sortOption}
          setSortOption={setSortOption}
          filters={filters}
          setFilters={setFilters}
          resetFilters={resetFilters}
        />

        <SelectedFilters
          filters={filters}
          setFilters={setFilters}
          resetFilters={resetFilters}
        />

        <div className={'grid grid-cols-2 md:grid-cols-4 gap-4 mt-4'}>

          {paginatedProducts.length === 0 ? (
            <Empty description={'No products found'} />
          ) : (
            paginatedProducts.map(product => {

              const firstSize = product?.size?.[0];

              const price = Number(firstSize?.price || 0);

              const offer = offers?.[0]?.applicableProductIds?.includes(product.id)
                ? offers[0]
                : null;

              const discountedPrice = offer
                ? Math.round(price - (price * offer.maxDiscount) / 100)
                : price;

              const hasDiscount = discountedPrice < price;

              return (
                <div
                  key={product.id}
                  className={'shadow rounded-lg p-3 hover:shadow-md transition'}
                >

                  <Link
                    to={`/product/${product.id}`}
                    state={{product}}
                  >
                    <img
                      src={
                        Array.isArray(product.imageUrl)
                          ? product.imageUrl[0]
                          : product.imageUrl
                      }
                      alt={product.productName}
                      className={'w-full h-48 object-cover rounded'}
                    />
                  </Link>

                  <h3 className={'mt-2 font-semibold capitalize'}>
                    {product.productName}
                  </h3>

                  <div className={'mt-1'}>

                    <span className={'text-xl font-bold text-primary-900'}>
                      ₹ {discountedPrice}
                    </span>

                    {hasDiscount && (
                      <>
                        <span className={'ml-2 text-red-500 font-semibold'}>
                          ({offer.maxDiscount}% OFF)
                        </span>

                        <div className={'line-through text-gray-400 text-sm'}>
                          ₹ {price}
                        </div>
                      </>
                    )}

                  </div>

                </div>
              );

            })
          )}

        </div>

        {sortedProducts.length > pageSize && (
          <div className={'mt-6 flex justify-end'}>

            <Pagination
              current={currentPage}
              pageSize={pageSize}
              total={sortedProducts.length}
              onChange={handlePageChange}
            />

          </div>
        )}

      </div>
    </div>
  );
};

export default ProductScreen;
